// The property management hub.
//
// A manager opens this to answer one question: what needs attention today. So the
// page leads with the things that carry a penalty — overdue statutory deadlines,
// properties being managed without an agreement, trust accounts out of balance —
// and puts the portfolio summary underneath. Anything that is merely interesting
// waits its turn.

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import {
    PageHeader, Panel, Stat, Pill, Table, Notice, Loading, Empty,
    fmtDate, titleCase, statusTone,
} from '../../components/secure/pm/PMKit';
import PMRulesAndFees from '../../components/secure/pm/PMRulesAndFees';
import { AboutThisPage } from '../../components/shared/PageHeader';

export default function PropertyManagementPage() {
    const apiClient = useApiClient();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [properties, setProperties] = useState([]);
    const [owners, setOwners] = useState([]);
    const [deadlines, setDeadlines] = useState({ overdue: [], due_soon: [], upcoming: [] });
    const [integrity, setIntegrity] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, o, d] = await Promise.all([
                apiClient.get('/api/property-management/properties'),
                apiClient.get('/api/property-management/owners'),
                apiClient.get('/api/property-management/deadlines?within_days=120'),
            ]);
            setProperties(p || []);
            setOwners(o || []);
            setDeadlines(d || { overdue: [], due_soon: [], upcoming: [] });
            // The integrity check is managing-broker only; a 403 here is expected
            // for everyone else and is not an error worth showing.
            try { setIntegrity(await apiClient.get('/api/property-management/verify')); } catch { /* not permitted */ }
        } catch (e) {
            setError(e.message || 'Could not load the property management overview.');
        } finally { setLoading(false); }
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Loading />;

    const managed = properties.filter((p) => p.status === 'managed');
    const units = properties.reduce((s, p) => s + Number(p.unit_count || 0), 0);
    const occupied = properties.reduce((s, p) => s + Number(p.occupied_count || 0), 0);
    const occupancy = units ? Math.round((occupied / units) * 100) : 0;
    const failedChecks = (integrity?.checks || []).filter((c) => !c.pass);

    return (
        <div>
            <PageHeader
                title="Property Management"
                subtitle="Portfolio, tenancies and client trust at a glance."
            />
            <AboutThisPage>
                <p>Each owner keeps their own set of books; the brokerage ledger references them. Overdue statutory deadlines and failing integrity checks are shown first.</p>
            </AboutThisPage>

            {error && <Notice tone="bad" message={error} onDismiss={() => setError('')} />}

            {/* What carries a penalty comes first. */}
            {deadlines.overdue.length > 0 && (
                <Notice
                    tone="bad"
                    title={`${deadlines.overdue.length} statutory deadline${deadlines.overdue.length === 1 ? '' : 's'} overdue`}
                    message="Nearly every landlord-tenant liability in Illinois is a missed date, and the penalties are not proportionate to the oversight. Clear these first."
                />
            )}
            {failedChecks.length > 0 && (
                <Notice
                    tone="warn"
                    title="Integrity checks failing"
                    problems={failedChecks.map((c) => `${c.check} — ${c.detail}`)}
                />
            )}

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
                <Stat label="Managed" value={managed.length}
                    sub={`${properties.length} on file`} />
                <Stat label="Units" value={units} sub={`${occupied} occupied`} />
                <Stat label="Occupancy" value={`${occupancy}%`}
                    tone={occupancy >= 95 ? 'good' : occupancy >= 85 ? 'default' : 'warn'} />
                <Stat label="Clients" value={owners.length} sub="each with a book" />
                <Stat label="Overdue" value={deadlines.overdue.length}
                    tone={deadlines.overdue.length ? 'bad' : 'good'}
                    sub={`${deadlines.due_soon.length} due within 14 days`} />
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
                <Panel
                    className="lg:col-span-2"
                    title="Needs attention"
                    subtitle="Overdue first, then what falls due inside a fortnight"
                    actions={<Link to="/dashboard/pm-deadlines"
                        className="text-xs text-indigo-600 hover:underline">Compliance calendar →</Link>}
                >
                    <Table
                        columns={[
                            { key: 'due_on', label: 'Due', nowrap: true,
                                render: (r) => (
                                    <div>
                                        <div className="font-medium text-slate-800">{fmtDate(r.due_on)}</div>
                                        <div className={`text-xs ${r.days_out < 0 ? 'text-rose-600 font-semibold' : 'text-slate-400'}`}>
                                            {r.days_out < 0 ? `${Math.abs(r.days_out)} days overdue` : `in ${r.days_out} days`}
                                        </div>
                                    </div>
                                ) },
                            { key: 'label', label: 'Obligation',
                                render: (r) => (
                                    <div>
                                        <div className="text-slate-800">{r.label}</div>
                                        <div className="text-xs text-slate-400 mt-0.5">
                                            {r.property_name}{r.unit_number ? ` · ${r.unit_number}` : ''}
                                        </div>
                                    </div>
                                ) },
                            { key: 'jurisdiction', label: 'Authority', nowrap: true,
                                render: (r) => r.jurisdiction
                                    ? <Pill tone="indigo">{r.jurisdiction}</Pill>
                                    : <span className="text-slate-300">—</span> },
                        ]}
                        rows={[...deadlines.overdue, ...deadlines.due_soon].slice(0, 12)
                            .map((r, i) => ({ ...r, _key: r.deadline_id || i }))}
                        rowTone={(r) => (r.days_out < 0 ? 'bad' : 'warn')}
                        empty="Nothing overdue and nothing due inside a fortnight."
                    />
                </Panel>

                <Panel title="Clients" subtitle="One book each"
                    actions={<Link to="/dashboard/pm-owners"
                        className="text-xs text-indigo-600 hover:underline">All →</Link>}>
                    {owners.length === 0
                        ? <Empty title="No clients yet"
                            message="An owner opens a book the moment they are created — nothing can be posted without one."
                            action={<Link to="/dashboard/pm-owners"
                                className="text-sm text-indigo-600 hover:underline">Add the first owner →</Link>} />
                        : (
                            <ul className="divide-y divide-slate-100 -my-2">
                                {owners.slice(0, 8).map((o) => (
                                    <li key={o.owner_id} className="py-2.5 flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <Link to={`/dashboard/pm-owners?owner=${o.owner_id}`}
                                                className="text-sm text-slate-800 hover:text-indigo-700 truncate block">
                                                {o.legal_name}
                                            </Link>
                                            <div className="text-xs text-slate-400">
                                                {o.book_no} · {o.property_count} propert{o.property_count === 1 ? 'y' : 'ies'}
                                            </div>
                                        </div>
                                        {!o.w9_on_file && <Pill tone="warn">no W-9</Pill>}
                                    </li>
                                ))}
                            </ul>
                        )}
                </Panel>
            </div>

            <div className="mt-5">
                <Panel title="Portfolio" subtitle="Rule layers resolve per property, and per lease within it"
                    actions={<Link to="/dashboard/pm-portfolio"
                        className="text-xs text-indigo-600 hover:underline">Manage →</Link>}>
                    <Table
                        columns={[
                            { key: 'name', label: 'Property',
                                render: (r) => (
                                    <div>
                                        <Link to={`/dashboard/pm-portfolio?property=${r.pm_property_id}`}
                                            className="text-slate-800 hover:text-indigo-700 font-medium">{r.name}</Link>
                                        <div className="text-xs text-slate-400">{r.street_address}, {r.city}</div>
                                    </div>
                                ) },
                            { key: 'owners', label: 'Owner', render: (r) => r.owners || '—' },
                            { key: 'asset_class', label: 'Class', nowrap: true,
                                render: (r) => <Pill tone={r.asset_class === 'mixed' ? 'violet' : 'slate'}>
                                    {r.asset_class}</Pill> },
                            { key: 'jur', label: 'Jurisdiction', nowrap: true,
                                render: (r) => (
                                    <div className="flex gap-1">
                                        {r.municipal_name && <Pill tone="bad">{r.municipal_name}</Pill>}
                                        {r.county_name && <Pill tone="warn">RTLO</Pill>}
                                        {!r.municipal_name && !r.county_name &&
                                            <span className="text-xs text-slate-400">state only</span>}
                                    </div>
                                ) },
                            { key: 'unit_count', label: 'Units', align: 'right',
                                render: (r) => `${r.occupied_count}/${r.unit_count}` },
                            { key: 'status', label: 'Status', nowrap: true,
                                render: (r) => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                        ]}
                        rows={properties.map((p) => ({ ...p, _key: p.pm_property_id }))}
                        empty="No properties yet. A property needs an owner, because it needs a book to post to."
                    />
                </Panel>
            </div>

            {/* Managing broker / admin only; renders nothing for anyone else. */}
            <div className="mt-5">
                <PMRulesAndFees />
            </div>
        </div>
    );
}
